import { ApiServices } from './apiServices';
import { ApiConfig } from './ApiConfigs';

/**
 * Update delivery status of an order
 * @param {number} orderId - Order ID
 * @param {object} statusData - Status update data
 * @param {string} statusData.status - Delivery status (PICKED_UP, IN_TRANSIT, OUT_FOR_DELIVERY, DELIVERED, ATTEMPTED_DELIVERY, FAILURE)
 * @param {string} statusData.message - Optional message
 * @param {number} statusData.latitude - Optional latitude
 * @param {number} statusData.longitude - Optional longitude
 * @returns {Promise<object>} - API response
 */
export const updateDeliveryStatus = async (orderId, statusData) => {
    try {
        const { status, message, latitude, longitude } = statusData;
        
        if (!status) {
            throw new Error('Status is required');
        }

        const validStatuses = ['PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'DELIVERED', 'ATTEMPTED_DELIVERY', 'FAILURE', 'RETURNED'];
        if (!validStatuses.includes(status.toUpperCase())) {
            throw new Error(`Invalid status. Valid values: ${validStatuses.join(', ')}`);
        }

        const body = {
            status: status.toUpperCase(),
            ...(message && { message }),
            ...(latitude !== undefined && { latitude }),
            ...(longitude !== undefined && { longitude })
        };

        const response = await ApiServices(
            'PUT',
            `${ApiConfig.ORDERS}/${orderId}/delivery-status`,
            body
        );

        return response;
    } catch (error) {
        console.error('Error updating delivery status:', error);
        throw error;
    }
};

/**
 * Get order by ID
 * @param {number} orderId - Order ID
 * @returns {Promise<object>} - Order data
 */
export const getOrderById = async (orderId) => {
    try {
        const response = await ApiServices('GET', `${ApiConfig.ORDERS}/${orderId}`);
        return response;
    } catch (error) {
        console.error('Error fetching order:', error);
        throw error;
    }
};

/**
 * Update order
 * @param {number} orderId - Order ID
 * @param {object} orderData - Order data to update
 * @returns {Promise<object>} - API response
 */
export const updateOrder = async (orderId, orderData) => {
    try {
        const response = await ApiServices('PUT', `${ApiConfig.ORDERS}/${orderId}`, orderData);
        return response;
    } catch (error) {
        console.error('Error updating order:', error);
        throw error;
    }
};

/**
 * Update order payment
 * @param {number} orderId - Order ID
 * @param {object} paymentData - { paymentStatus?, paymentReceive?, paymentType? }
 * @returns {Promise<object>} - API response
 */
export const updatePayment = async (orderId, paymentData) => {
    try {
        const response = await ApiServices('PUT', `${ApiConfig.ORDERS}/${orderId}/payment`, paymentData);
        return response;
    } catch (error) {
        console.error('Error updating payment:', error);
        throw error;
    }
};

/**
 * Get all orders
 * @param {object} filters - Optional filters
 * @returns {Promise<object>} - Orders data
 */
export const getAllOrders = async (filters = {}) => {
    try {
        const queryParams = new URLSearchParams(filters).toString();
        const url = queryParams ? `${ApiConfig.ORDERS}?${queryParams}` : ApiConfig.ORDERS;
        const response = await ApiServices('GET', url);
        return response;
    } catch (error) {
        console.error('Error fetching orders:', error);
        throw error;
    }
};

/**
 * Order status constants
 */
export const ORDER_STATUS = {
    PENDING: 'PENDING',
    CONFIRMED: 'CONFIRMED',
    PROCESSING: 'PROCESSING',
    READY_FOR_PICKUP: 'READY_FOR_PICKUP',
    PICKED_UP: 'PICKED_UP',
    IN_TRANSIT: 'IN_TRANSIT',
    OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
    DELIVERED: 'DELIVERED',
    COMPLETED: 'COMPLETED',
    PARTIAL_DELIVERED: 'PARTIAL_DELIVERED',
    PARTIAL_IN_TRANSIT: 'PARTIAL_IN_TRANSIT',
    PARTIAL_PICKED_UP: 'PARTIAL_PICKED_UP',
    PARTIAL_READY_TO_PICKUP: 'PARTIAL_READY_TO_PICKUP',
    CLOSED: 'CLOSED',
    CANCELLED: 'CANCELLED',
    REFUNDED: 'REFUNDED',
    FAILED: 'FAILED',
    RETURNED: 'RETURNED'
};

/**
 * Payment status constants
 */
export const PAYMENT_STATUS = {
    PENDING: 'PENDING',
    PAID: 'PAID',
    UNPAID: 'UNPAID',
    PARTIALLY_PAID: 'PARTIALLY_PAID',
    REFUNDED: 'REFUNDED'
};

/**
 * Delivery status constants
 */
export const DELIVERY_STATUS = {
    PICKED_UP: 'PICKED_UP',
    IN_TRANSIT: 'IN_TRANSIT',
    OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
    DELIVERED: 'DELIVERED',
    ATTEMPTED_DELIVERY: 'ATTEMPTED_DELIVERY',
    FAILURE: 'FAILURE'
};

/**
 * Payment type constants
 */
export const PAYMENT_TYPE = {
    COD: 'COD',
    PREPAID: 'PREPAID',
    PARTIAL: 'PARTIAL',
    CREDIT: 'CREDIT'
};

/**
 * Get status color for UI display
 * @param {string} status - Order status
 * @returns {string} - Color code
 */
export const getStatusColor = (status) => {
    const statusColors = {
        'PENDING': 'orange',
        'CONFIRMED': 'blue',
        'PROCESSING': 'cyan',
        'READY_FOR_PICKUP': 'purple',
        'PICKED_UP': 'geekblue',
        'IN_TRANSIT': 'blue',
        'OUT_FOR_DELIVERY': 'lime',
        'DELIVERED': 'green',
        'COMPLETED': 'green',
        'PARTIAL_DELIVERED': 'green',
        'PARTIAL_IN_TRANSIT': 'gold',
        'PARTIAL_PICKED_UP': 'blue',
        'PARTIAL_READY_TO_PICKUP': 'purple',
        'CLOSED': 'default',
        'CANCELLED': 'red',
        'REFUNDED': 'volcano',
        'FAILED': 'red',
        'RETURNED': 'orange'
    };
    return statusColors[status] || 'default';
};

/**
 * Get payment status color for UI display
 * @param {string} paymentStatus - Payment status
 * @returns {string} - Color code
 */
export const getPaymentStatusColor = (paymentStatus) => {
    const colors = {
        'PENDING': 'orange',
        'PAID': 'green',
        'UNPAID': 'red',
        'PARTIALLY_PAID': 'blue',
        'REFUNDED': 'volcano'
    };
    return colors[paymentStatus] || 'default';
};
